import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, fromEvent, firstValueFrom } from 'rxjs';
import { stringify } from 'flatted';

import { PORTAL_SERVER_URL } from '../../config';
import { LogEntry, LogLevel } from '../models/log-entry';

import { LogDB } from './log-db.database';
import { AnalyticsService } from './analytics.service';
import { ConnectionService } from './connection.service';

const PORTAL_POST_LOG = `${ PORTAL_SERVER_URL }/clientLog:log`;

@Injectable({ providedIn: 'root' })
export class LogService implements OnDestroy {
	private readonly subOnConnect: Subscription;

	constructor(
		private readonly logDB: LogDB,
		private readonly http: HttpClient,
		private readonly connectionService: ConnectionService,
		private readonly analyticsService: AnalyticsService,
		private readonly router: Router,
		private readonly ngZone: NgZone
	) {
		// push log entries every time back online
		this.subOnConnect = fromEvent(globalThis, 'online').subscribe(() => {
			this.pushLogEntries().catch((err: unknown) => {
				console.error(err);
			});
		});

		// push log entries at initialization
		this.pushLogEntries().catch((err: unknown) => {
			console.error(err);
		});

		// clear log entries when disconnected
		this.connectionService.disconnectedTasks.push(this.disconnectedTask);
	}

	ngOnDestroy() {
		this.subOnConnect.unsubscribe();
		const disconIdx = this.connectionService.disconnectedTasks.indexOf(this.disconnectedTask);
		this.connectionService.disconnectedTasks.splice(disconIdx, 1);
	}

	error(e: unknown) {
		if (typeof e === 'object' && e && 'rejection' in e) {
			console.error((<{ rejection: unknown }> e).rejection, '\n', e);
		} else {
			console.error(e);
		}
		this.ngZone.runOutsideAngular(() => {
			this.log(e).catch((err: unknown) => {
				console.error(err);
			});
		});
	}

	private async log(error: unknown) {
		this.analyticsService.trackException(error);

		if (await this.connectionService.getAccessToken() === null) {
			return;
		}
		const { url } = this.router;
		const level = LogLevel.ERROR;
		let err = error;
		let fullErrorStr = '';
		do {
			const realError = typeof err === 'object' && err && 'rejection' in err
				? (<{ rejection: unknown }> err).rejection
				: err;
			const errorStr = typeof realError === 'object' && realError && 'stack' in realError
				? typeof realError.stack === 'string' ? realError.stack : stringify(realError.stack)
				: typeof realError === 'string' ? realError : stringify(realError);
			fullErrorStr += errorStr;
			err = typeof err === 'object' && err && 'cause' in err ? err.cause : undefined;
			if (err) {
				fullErrorStr += '\nCaused by: ';
			}
		} while (err);
		const logEntry = new LogEntry(url, level, fullErrorStr);

		if (!navigator.onLine) {
			this.addLogEntryInDb(logEntry);
			return;
		}
		try {
			await this.sendLogEntries([logEntry]);
		} catch (errorPostLog) {
			console.error('unable to send log entry to server', errorPostLog);
			this.addLogEntryInDb(logEntry);
		}
	}

	private addLogEntryInDb(logEntry: LogEntry) {
		try {
			this.logDB.addLogEntry(logEntry);
		} catch (err) {
			console.error('unable to save log entry locally', err);
		}
	}

	private async pushLogEntries() {
		try {
			const entries = this.logDB.removeLogEntries();
			if (entries.length === 0) {
				return;
			}
			if (await this.connectionService.getAccessToken() === null) {
				console.info(`${ entries.length } log entries dropped`);
				return;
			}
			try {
				await this.sendLogEntries(entries);
			} catch (errorSend) {
				console.error('error sending log entries', errorSend);
				this.logDB.addLogEntries(entries);
			}
		} catch (error) {
			console.error('error pushing log entries', error);
		}
	}

	private async sendLogEntries(logEntries: LogEntry[]) {
		await firstValueFrom(this.http.post(PORTAL_POST_LOG, logEntries));
	}

	// eslint-disable-next-line @typescript-eslint/require-await
	private readonly disconnectedTask = async () => {
		const entries = this.logDB.removeLogEntries();
		if (entries.length > 0) {
			console.info(`${ entries.length } log entries dropped`);
		}
	};
}
